import type { TableProps } from '@aibees/shared/components/table';
import { useDebounce } from '@aibees/shared/hooks';
import useSearchItemsStore from '@aibees/shared/store/search-items-store/search-items-store';
import useTableDataFetchingStore from '@aibees/shared/store/table-data-fetching/table-data-fetching-store';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { BrowseApiDataResult } from '../../../browse-data-types';
import { useAdvanceTableDataContext } from '../../../context';
import type { AdvanceTableConfig, AdvanceTableFilters } from '../../../types';
import {
  calculateTableSort,
  getUpdatedSearchContent,
  toBrowseApiData,
} from '../utils';

export type { BrowseApiDataResult } from '../../../browse-data-types';

// Consolidate pagination state into a single object
interface PaginationState {
  startingAfter?: string;
  endingBefore?: string;
  firstRowId?: string;
  lastRowId?: string;
  enabledNextButton: boolean;
  enabledPrevButton: boolean;
}

/**
 * Core browse-data hook that takes explicit dependencies (no context read).
 * Called once by AdvanceTableContextProvider to avoid duplicate invocations.
 */
export function useBrowseApiDataCore(
  config: AdvanceTableConfig,
  filters: AdvanceTableFilters,
  table: TableProps
): BrowseApiDataResult {
  const debouncedFilters = useDebounce(filters, 100);
  const updatedSearchItems = useSearchItemsStore((s) => s.updatedSearchItems);
  const [paginationState, setPaginationState] = useState<PaginationState>({
    enabledNextButton: false,
    enabledPrevButton: false,
  });
  const setIsLoading = useTableDataFetchingStore((s) => s.setIsLoading);

  const isSequentialPagination = config?.sequentialPagination ?? false;

  const { page: tablePage, rowsPerPage, orderBy, order } = table;
  const { extraParam } = table.selection;
  const sortStr = useMemo(
    () => calculateTableSort(table, config.prependSortingFields),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [orderBy, order, config.prependSortingFields]
  );

  const regularPaginationParams = useMemo(
    () => ({
      sort: sortStr,
      ...debouncedFilters,
      ...config.extraFilters,
      ...extraParam,
      page: tablePage,
      size: rowsPerPage,
    }),
    [
      sortStr,
      debouncedFilters,
      config.extraFilters,
      extraParam,
      tablePage,
      rowsPerPage,
    ]
  );

  const params = useMemo(() => {
    if (isSequentialPagination) {
      return {
        sort: sortStr,
        ...debouncedFilters,
        ...config.extraFilters,
        ...extraParam,
        size: rowsPerPage,
        ...(paginationState.startingAfter && {
          startingAfter: paginationState.startingAfter,
        }),
        ...(paginationState.endingBefore && {
          endingBefore: paginationState.endingBefore,
        }),
      };
    }
    return regularPaginationParams;
  }, [
    isSequentialPagination,
    regularPaginationParams,
    sortStr,
    config.extraFilters,
    debouncedFilters,
    extraParam,
    rowsPerPage,
    paginationState.startingAfter,
    paginationState.endingBefore,
  ]);

  const { data, isLoading, refetch, isFetching } =
    typeof config.browseApi === 'function'
      ? config.browseApi(params)
      : toBrowseApiData(config.browseApi, table);

  // Memoize the update function to prevent unnecessary re-renders
  const updatePaginationState = useCallback(
    (updates: Partial<PaginationState>) => {
      setPaginationState((prev) => ({ ...prev, ...updates }));
    },
    []
  );

  // Compute merged data synchronously to avoid extra table render (no effect + setState)
  const effectiveData = useMemo(() => {
    if (!data) return undefined;
    if (config.enableUpdatedSearchItems && updatedSearchItems.length) {
      return {
        ...data,
        content: getUpdatedSearchContent(
          data.content ?? [],
          updatedSearchItems
        ),
        top: getUpdatedSearchContent(data.top ?? [], updatedSearchItems),
      };
    }
    return data;
  }, [data, config.enableUpdatedSearchItems, updatedSearchItems]);

  // Optimize loading state effect
  useEffect(() => {
    setIsLoading(!!isLoading);
  }, [isLoading, setIsLoading]);

  // Separate effects for different concerns to reduce unnecessary re-renders
  useEffect(() => {
    if (!data?.content || !isSequentialPagination) return;

    const firstRowId = data.content[0]?.id;
    const lastRowId = data.content[data.content.length - 1]?.id;

    const enabledNextButton =
      (data?.startingAfter != null && data.hasMore === true) ||
      data?.endingBefore != null ||
      data?.startingAfter === null;

    const enabledPrevButton =
      (data?.endingBefore != null && data.hasMore === true) ||
      data?.startingAfter != null;

    // Only update state if values actually changed
    updatePaginationState({
      firstRowId,
      lastRowId,
      enabledNextButton:
        !data?.endingBefore && !data?.startingAfter && !data.hasMore
          ? false
          : enabledNextButton,
      enabledPrevButton:
        !data?.endingBefore && !data?.startingAfter && !data.hasMore
          ? false
          : enabledPrevButton,
    });
  }, [data, isSequentialPagination, updatePaginationState]);

  // Memoize pagination handlers to prevent unnecessary re-renders
  const handleNextPage = useCallback(() => {
    updatePaginationState({
      endingBefore: undefined,
      startingAfter: paginationState.lastRowId,
    });
  }, [paginationState.lastRowId, updatePaginationState]);

  const handlePreviousPage = useCallback(() => {
    updatePaginationState({
      startingAfter: undefined,
      endingBefore: paginationState.firstRowId,
    });
  }, [paginationState.firstRowId, updatePaginationState]);

  // Optimize refetch effect
  useEffect(() => {
    if (
      refetch &&
      (paginationState.startingAfter !== undefined ||
        paginationState.endingBefore !== undefined)
    ) {
      refetch();
    }
  }, [paginationState.startingAfter, paginationState.endingBefore, refetch]);

  return useMemo(
    () => ({
      data: effectiveData,
      isLoading,
      isFetching,
      refetch,
      handleNextPage,
      handlePreviousPage,
      enabledNextButton: paginationState.enabledNextButton,
      enabledPrevButton: paginationState.enabledPrevButton,
    }),
    [
      effectiveData,
      isLoading,
      isFetching,
      refetch,
      handleNextPage,
      handlePreviousPage,
      paginationState.enabledNextButton,
      paginationState.enabledPrevButton,
    ]
  );
}

/**
 * Reads browse data from context. The data is fetched once by the provider
 * via useBrowseApiDataCore, eliminating duplicate invocations across the tree.
 *
 * @deprecated Prefer reading `browseData` from `useAdvanceTableContext()` directly.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function useGetBrowseApiData(_table?: TableProps): BrowseApiDataResult {
  const { browseData } = useAdvanceTableDataContext();
  return browseData;
}
